import React, { useEffect, useMemo, useCallback } from 'react';
import {
  NotificationCard,
  NotificationFilters,
} from './components';
import type { FilterValue } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { useNotificationsStore } from '../../../store/notificationsStore';
import { Notification, NotificationType } from '../../../services/notificationsApi';
import { NotificationItem, filterNotifications } from './notificationsTypes';
import styles from './CustomerNotifications.module.css';

function relativeTimestamp(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'Yesterday';
  if (d < 7) return `${d} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function adapt(n: Notification): NotificationItem {
  return {
    id: String(n.id),
    title: n.title,
    message: n.message,
    type: n.type,
    timestamp: relativeTimestamp(n.created_at),
    isRead: n.is_read,
  };
}

const CustomerNotifications: React.FC = () => {
  const items = useNotificationsStore((s) => s.items);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const loading = useNotificationsStore((s) => s.loading);
  const error = useNotificationsStore((s) => s.error);
  const fetchList = useNotificationsStore((s) => s.fetchList);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);

  const [activeFilter, setActiveFilter] = React.useState<FilterValue>('all');

  useEffect(() => { fetchList(); }, [fetchList]);

  const adapted = useMemo<NotificationItem[]>(() => items.map(adapt), [items]);
  const filtered = useMemo(() => {
    const filterArg: 'all' | 'unread' | NotificationType =
      activeFilter === 'all' || activeFilter === 'unread'
        ? activeFilter
        : (activeFilter as NotificationType);
    return filterNotifications(adapted, filterArg);
  }, [adapted, activeFilter]);

  const handleMarkAsRead = useCallback((id: string) => {
    void markRead(Number(id));
  }, [markRead]);

  const handleMarkAllAsRead = useCallback(() => {
    void markAllRead();
  }, [markAllRead]);

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Notifications</h1>
          <p className={styles.pageSubtitle}>
            Stay updated with your orders, payments, and deliveries
          </p>
        </div>
        {unreadCount > 0 && (
          <button className={styles.markAllBtn} onClick={handleMarkAllAsRead}>
            Mark all as read
          </button>
        )}
      </div>

      <NotificationFilters
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
      />

      <div className={styles.content}>
        <AsyncBoundary
          loading={loading && items.length === 0}
          error={error}
          empty={!loading && filtered.length === 0}
          emptyMessage="No notifications yet"
          onRetry={() => fetchList()}
        >
          <NotificationCard
            notifications={filtered}
            unreadCount={unreadCount}
            onMarkAsRead={handleMarkAsRead}
            maxItems={filtered.length}
          />
        </AsyncBoundary>
      </div>
    </div>
  );
};

export const CustomerNotificationsPage: React.FC = () => {
  return <CustomerNotifications />;
};

export { CustomerNotifications };
export default CustomerNotifications;
